import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef } from "react";

interface Props<V extends number | string> {
  label: string;
  options: Array<{
    id: V;
    label: string;
  }>;
  value: number | string | null | undefined;
  onChange: (value: V | null) => void;
  cancelable: boolean;
  disabled: boolean;
}

export function AddCardToBinderOptions<V extends number | string>({
  label,
  options,
  value,
  onChange,
  cancelable,
  disabled,
}: Props<V>) {
  const activeElementRef = useRef<HTMLButtonElement | null>(null);

  const onClick = useCallback(
    (id: V, isActive: boolean) => {
      if (isActive && !cancelable) {
        return;
      }

      if (isActive) {
        onChange(null);
      } else {
        onChange(id);
      }
    },
    [cancelable, onChange],
  );

  useEffect(() => {
    setTimeout(() => {
      if (activeElementRef.current) {
        activeElementRef.current.scrollIntoView();
      }
    }, 0);
  }, []);

  return (
    <div className="grid gap-2">
      <p className="font-medium">{label}</p>
      <div className="flex gap-2 overflow-x-auto ">
        {options.map((option) => (
          <button
            key={option.id}
            ref={(el) => {
              if (el && value === option.id) {
                activeElementRef.current = el;
              }
            }}
            onClick={() => onClick(option.id, value === option.id)}
            className={cn(
              "border border-input rounded-lg px-2 py-0.5 whitespace-nowrap ",
              {
                "bg-[#2b64ea] text-white": value === option.id,
                "opacity-90": disabled,
              },
            )}
            disabled={disabled}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
